<template>
  <button
    class="product-actions wc-card-hover"
    type="button"
    :id="'coupon_' + couponId"
    :class="{ clipped: isClippedOrRedeemed }"
    @click="handleClick"
    :aria-pressed="isClipped || isRedeemed ? 'true' : 'false'"
    :disabled="isDisabled || isClippedOrRedeemed"
  >
    <font-awesome-icon
      v-if="!isClipped && !isRedeemed"
      icon="scissors"
      class="product-add-icon"
      aria-hidden="true"
    />
    <font-awesome-icon v-else icon="check" class="product-add-icon" aria-hidden="true" />
    <span class="product-add-label">{{ label }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed, defineProps, withDefaults } from 'vue';
import { COUPON_STATUS_FILTERS } from '@/constants/CouponConstants';

interface Props {
  couponId: string;
  availableLabel: string;
  clippedLabel: string;
  redeemedLabel: string;
  couponFilter?: string;
  isClipped?: boolean;
  isRedeemed?: boolean;
  disabled?: boolean;
  handleClip?: Function;
}

const props = withDefaults(defineProps<Props>(), {
  couponFilter: undefined,
  isClipped: false,
  isRedeemed: false,
  disabled: false,
  handleClip: () => {},
});

const isDisabled = computed(() => props.disabled);

const isClippedOrRedeemed = computed(
  () =>
    props.isClipped ||
    props.isRedeemed ||
    props.couponFilter === COUPON_STATUS_FILTERS.CLIPPED ||
    props.couponFilter === COUPON_STATUS_FILTERS.REDEEMED,
);

const label = computed({
  get: () => {
    if (props.couponFilter === COUPON_STATUS_FILTERS.CLIPPED) {
      return props.clippedLabel;
    }
    if (props.couponFilter === COUPON_STATUS_FILTERS.REDEEMED) {
      return props.redeemedLabel;
    }
    if (props.isClipped) {
      return props.clippedLabel;
    }
    if (props.isRedeemed) {
      return props.redeemedLabel;
    }
    return props.availableLabel;
  },
  set: newLabel => newLabel,
});

const handleClick = (event: MouseEvent) => {
  if (!props.isClipped) {
    props.handleClip(props.couponId, event.currentTarget as HTMLElement);
  }
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.product-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  height: convert-px-to-rem(36);
  margin: 0 convert-px-to-rem(12) convert-px-to-rem(16);
  padding: 0 1rem;
  background: var(--white, $white);
  border: 1.5px solid var(--primary, $primary);
  border-radius: 999px;
  font-size: $font-size-sm;
  font-weight: 700;
  color: var(--primary, $primary);
  cursor: pointer;
}

.product-actions.clipped,
.product-actions:disabled {
  cursor: default;
  pointer-events: none;
  border-color: #c8cdd8;
}

.product-actions:hover:not(:disabled):not(.clipped) {
  background: var(--primary-lighter-9);
}

.product-actions.clipped .product-add-icon,
.product-actions.clipped .product-add-label,
.product-actions:disabled .product-add-icon,
.product-actions:disabled .product-add-label {
  color: #7a8499;
}

.product-add-icon {
  font-size: $font-size-sm;
}

.product-add-label {
  font-size: $font-size-sm;
}
</style>
